import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  People as PeopleIcon,
  Group as GroupIcon,
  Image as ImageIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  PhoneIphone as PhoneIphoneIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  GridOn as GridOnIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  Fingerprint as FingerprintIcon,
  Code as CodeIcon,
  Download as DownloadIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { Group } from '../models/Group';
import { adminApi, groupApi } from '../utils/api';
import { BASE_URL } from '../constants/global';

const useAdminData = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const adminCheck = await adminApi.checkSuperAdmin(token);
      if (!adminCheck.isSuperAdmin) {
        return { isSuperAdmin: false, stats: null, users: [], groups: [], featureToggles: [], featuredCollections: [], allCollections: [], announcements: [] };
      }
      const [statsData, usersData, groupsData, togglesData, featuredData, collectionsData, announcementsData] = await Promise.all([
        adminApi.getStats(token),
        adminApi.getAllUsers(token),
        adminApi.getAllGroups(token),
        fetch(`${BASE_URL}/v1/feature-toggles`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${BASE_URL}/v1/featured-collections`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${BASE_URL}/v1/collections/my`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${BASE_URL}/v1/announcements`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []).catch(() => []),
      ]);
      return {
        isSuperAdmin: true,
        stats: statsData,
        users: usersData,
        groups: groupsData,
        featureToggles: Array.isArray(togglesData) ? togglesData : [],
        featuredCollections: Array.isArray(featuredData) ? featuredData : [],
        allCollections: Array.isArray(collectionsData) ? collectionsData : [],
        announcements: Array.isArray(announcementsData) ? announcementsData : [],
      };
    },
  });
};

const useErrorLogs = (params: any, enabled: boolean) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['admin', 'error-logs', params],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const [logsData, statsData, topUsersData, heatmapResult, rateData] = await Promise.all([
        adminApi.getErrorLogs(token, params),
        adminApi.getErrorStats(token),
        adminApi.getTopErrorUsers(token, 10),
        adminApi.getErrorHeatmap(token, 30),
        adminApi.getErrorRate(token),
      ]);
      return {
        logs: logsData.content || [],
        total: logsData.totalElements || 0,
        stats: statsData,
        topUsers: topUsersData || [],
        heatmap: heatmapResult || [],
        errorRate: rateData,
      };
    },
    enabled,
  });
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface UserData {
  id: number;
  email: string;
  name: string;
  givenName: string;
  affiliation: string;
  isSuperAdmin: boolean;
}

interface Stats {
  totalUsers: number;
  totalGroups: number;
  totalImages: number;
}

const AdminDashboard: React.FC = () => {
  const universityOptions = [
    "University of South Carolina",
    "Baylor University",
    "Florida International University",
    "National Institute of Health",
  ];

  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [editFormData, setEditFormData] = useState({ name: '', givenName: '', affiliation: '', isSuperAdmin: false });
  const [saving, setSaving] = useState(false);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [newToggle, setNewToggle] = useState({ featureName: 'featured_collections', groupId: '', enabled: true });
  const [featuredDialogOpen, setFeaturedDialogOpen] = useState(false);
  const [newFeatured, setNewFeatured] = useState({ collectionId: '', displayOrder: 0 });
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<any>(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    message: '',
    startDate: new Date().toISOString().slice(0, 16),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    severity: 'info',
    active: true
  });

  // Error Center state
  const [errorPage, setErrorPage] = useState(0);
  const [errorRowsPerPage, setErrorRowsPerPage] = useState(10);
  const [errorFilters, setErrorFilters] = useState({
    errorCode: '',
    errorType: '',
    isResolved: '',
    isIos: '',
    userEmail: '',
    isSuccess: '',
    correlationId: '',
    errorSource: '',
    startDate: '',
    endDate: '',
  });
  const [errorTimeRange, setErrorTimeRange] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [errorDetail, setErrorDetail] = useState<any>(null);
  const [errorDetailOpen, setErrorDetailOpen] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [correlationLogs, setCorrelationLogs] = useState<any[]>([]);
  const [correlationOpen, setCorrelationOpen] = useState(false);
  const [groupedView, setGroupedView] = useState(false);
  const [groupedErrors, setGroupedErrors] = useState<any[]>([]);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayResult, setReplayResult] = useState<any>(null);

  const { data: adminData, isLoading: loading, refetch: refetchAdmin } = useAdminData();
  
  const isSuperAdmin = adminData?.isSuperAdmin ?? null;
  const stats = adminData?.stats ?? null;
  const users = adminData?.users ?? [];
  const groups = adminData?.groups ?? [];
  const featureToggles = adminData?.featureToggles ?? [];
  const featuredCollections = adminData?.featuredCollections ?? [];
  const allCollections = adminData?.allCollections ?? [];
  const announcements = adminData?.announcements ?? [];

  // Build error query params
  const errorParams: any = { page: errorPage, size: errorRowsPerPage };
  if (errorFilters.errorCode) errorParams.errorCode = errorFilters.errorCode;
  if (errorFilters.errorType) errorParams.errorType = errorFilters.errorType;
  if (errorFilters.isResolved !== '') errorParams.isResolved = errorFilters.isResolved === 'true';
  if (errorFilters.isIos !== '') errorParams.isIos = errorFilters.isIos === 'true';
  if (errorFilters.userEmail) errorParams.userEmail = errorFilters.userEmail;
  if (errorFilters.isSuccess !== '') errorParams.isSuccess = errorFilters.isSuccess === 'true';
  if (errorFilters.correlationId) errorParams.correlationId = errorFilters.correlationId;
  if (errorFilters.errorSource) errorParams.errorSource = errorFilters.errorSource;
  if (errorFilters.startDate) errorParams.startDateTs = new Date(errorFilters.startDate).getTime();
  if (errorFilters.endDate) errorParams.endDateTs = new Date(errorFilters.endDate).getTime();
  if (errorTimeRange !== 'all' && !errorFilters.startDate) {
    const now = Date.now();
    const ranges: Record<string, number> = {
      '1h': 3600000,
      '6h': 21600000,
      '24h': 86400000,
      '7d': 604800000,
      '30d': 2592000000,
    };
    errorParams.startDateTs = now - ranges[errorTimeRange];
  }

  const { data: errorData, isLoading: errorLoading, refetch: refetchErrorLogs } = useErrorLogs(errorParams, tabValue === 4 && !!isSuperAdmin);
  
  const errorLogs = errorData?.logs ?? [];
  const errorTotal = errorData?.total ?? 0;
  const errorStats = errorData?.stats ?? null;
  const topUsers = errorData?.topUsers ?? [];
  const heatmapData = errorData?.heatmap ?? [];
  const errorRate = errorData?.errorRate ?? null;

  const handleToggleSuperAdmin = async (userId: number, currentValue: boolean) => {
    try {
      const token = await getAccessTokenSilently();
      await adminApi.toggleSuperAdmin(userId, !currentValue, token);
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to update super admin status');
    }
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      givenName: user.givenName || '',
      affiliation: user.affiliation || '',
      isSuperAdmin: user.isSuperAdmin || false,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setEditFormData({ name: '', givenName: '', affiliation: '', isSuperAdmin: false });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      
      // Update user details
      await fetch(`${BASE_URL}/v1/user/admin/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editFormData.name,
          givenName: editFormData.givenName,
          affiliation: editFormData.affiliation,
        }),
      });
      
      // Update super admin status if changed
      if (editFormData.isSuperAdmin !== editingUser.isSuperAdmin) {
        await adminApi.toggleSuperAdmin(editingUser.id, editFormData.isSuperAdmin, token);
      }
      
      // Invalidate cache to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      
      handleCloseEditDialog();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateToggle = async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/feature-toggles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newToggle),
      });
      if (!response.ok) throw new Error('Failed to create toggle');
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setToggleDialogOpen(false);
      setNewToggle({ featureName: 'featured_collections', groupId: '', enabled: true });
    } catch (err: any) {
      setError(err.message || 'Failed to create feature toggle');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFeature = async (id: number, enabled: boolean) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BASE_URL}/v1/feature-toggles/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: !enabled }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to toggle feature');
    }
  };

  const handleDeleteToggle = async (id: number) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BASE_URL}/v1/feature-toggles/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to delete toggle');
    }
  };

  const handleAddFeatured = async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/featured-collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newFeatured),
      });
      if (!response.ok) throw new Error('Failed to add featured collection');
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setFeaturedDialogOpen(false);
      setNewFeatured({ collectionId: '', displayOrder: 0 });
    } catch (err: any) {
      setError(err.message || 'Failed to add featured collection');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFeatured = async (id: number) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BASE_URL}/v1/featured-collections/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to remove featured collection');
    }
  };

  const handleUpdateDisplayOrder = async (id: number, displayOrder: number) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BASE_URL}/v1/featured-collections/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ displayOrder }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to update display order');
    }
  };

  const handleCreateAnnouncement = async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newAnnouncement),
      });
      if (!response.ok) throw new Error('Failed to create announcement');
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setAnnouncementDialogOpen(false);
      setNewAnnouncement({
        message: '',
        startDate: new Date().toISOString().slice(0, 16),
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        severity: 'info',
        active: true
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAnnouncement = async () => {
    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${BASE_URL}/v1/announcements/${editingAnnouncement.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingAnnouncement),
      });
      if (!response.ok) throw new Error('Failed to update announcement');
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setAnnouncementDialogOpen(false);
      setEditingAnnouncement(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAnnouncement = async (id: number) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BASE_URL}/v1/announcements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to delete announcement');
    }
  };

  const handleToggleAnnouncementActive = async (id: number, active: boolean) => {
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${BASE_URL}/v1/announcements/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ active: !active }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    } catch (err: any) {
      setError(err.message || 'Failed to toggle announcement');
    }
  };

  // Error Center handlers
  const handleErrorResponse = async (id: number) => {
    try {
      const token = await getAccessTokenSilently();
      const detail = await adminApi.getErrorLogById(id, token);
      setErrorDetail(detail);
      setResolveNotes(detail?.resolutionNotes || '');
      setReplayResult(null);
      setErrorDetailOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load error detail');
    }
  };

  const fetchGroupedErrors = async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await adminApi.getGroupedErrors(token, 20);
      setGroupedErrors(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load grouped errors');
    }
  };

  const handleReplay = async () => {
    if (!errorDetail) return;
    try {
      setReplayLoading(true);
      setReplayResult(null);
      const token = await getAccessTokenSilently();
      const result = await adminApi.replayAnalysis(errorDetail.id, token);
      setReplayResult(result);
    } catch (err: any) {
      setReplayResult({ error: true, message: err.message || 'Replay failed' });
    } finally {
      setReplayLoading(false);
    }
  };

  const handleResolveError = async () => {
    if (!errorDetail) return;
    try {
      const token = await getAccessTokenSilently();
      await adminApi.resolveError(errorDetail.id, resolveNotes, token);
      setErrorDetailOpen(false);
      setErrorDetail(null);
      setResolveNotes('');
      refetchErrorLogs();
    } catch (err: any) {
      setError(err.message || 'Failed to resolve error');
    }
  };

  const handleDeleteErrorLog = async (id: number) => {
    try {
      const token = await getAccessTokenSilently();
      await adminApi.deleteErrorLog(id, token);
      refetchErrorLogs();
    } catch (err: any) {
      setError(err.message || 'Failed to delete error log');
    }
  };

  const handleApplyErrorFilters = () => {
    setErrorPage(0);
    refetchErrorLogs();
  };

  const handleClearErrorFilters = () => {
    setErrorFilters({ errorCode: '', errorType: '', isResolved: '', isIos: '', userEmail: '', isSuccess: '', correlationId: '', errorSource: '', startDate: '', endDate: '' });
    setErrorTimeRange('all');
    setErrorPage(0);
  };

  useEffect(() => {
    if (!autoRefresh || tabValue !== 4) return;
    const interval = setInterval(() => {
      refetchErrorLogs();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, tabValue, refetchErrorLogs]);

  const handleExportCSV = useCallback(() => {
    if (!errorLogs || errorLogs.length === 0) return;
    const headers = ['ID', 'Created At', 'Error Code', 'Error Type', 'HTTP Status', 'Error Message', 'User Email', 'File Name', 'File Size', 'Is iOS', 'Is Resolved', 'Client IP', 'Correlation ID', 'Is Success', 'Error Source'];
    const rows = errorLogs.map((log: any) => [
      log.id,
      log.createdAt ? new Date(log.createdAt).toISOString() : '',
      log.errorCode || '',
      log.errorType || '',
      log.httpStatus || '',
      `"${(log.errorMessage || '').replace(/"/g, '""')}"`,
      log.userEmail || '',
      log.fileName || '',
      log.fileSizeBytes || '',
      log.isIos ? 'Yes' : 'No',
      log.isResolved ? 'Yes' : 'No',
      log.clientIp || '',
      log.correlationId || '',
      log.isSuccess ? 'Yes' : 'No',
      log.errorSource || '',
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [errorLogs]);

  const handleViewCorrelation = async (correlationId: string) => {
    try {
      const token = await getAccessTokenSilently();
      const logs = await adminApi.getErrorsByCorrelationId(correlationId, token);
      setCorrelationLogs(logs || []);
      setCorrelationOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load correlation logs');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <AdminIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography color="text.secondary">Super admin privileges are required to access this page.</Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate('/')}>
          Go Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4">Admin Dashboard</Typography>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={() => refetchAdmin()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <PeopleIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">{stats?.totalUsers || 0}</Typography>
              <Typography color="text.secondary">Total Users</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
              <GroupIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">{stats?.totalGroups || 0}</Typography>
              <Typography color="text.secondary">Total Groups</Typography>
            </Box>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
              <ImageIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">{stats?.totalImages || 0}</Typography>
              <Typography color="text.secondary">Total Images</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Users (${users.length})`} />
        <Tab label={`Groups (${groups.length})`} />
        <Tab label="Features" />
        <Tab label={`Announcements (${announcements.length})`} />
        <Tab label={`Error Center${errorStats ? ` (${errorStats.unresolvedErrors})` : ''}`} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <TextField
          fullWidth
          placeholder="Search users by name or email..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Affiliation</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users
                .filter(user => 
                  userSearch === '' ||
                  user.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
                  user.givenName?.toLowerCase().includes(userSearch.toLowerCase()) ||
                  user.email?.toLowerCase().includes(userSearch.toLowerCase())
                )
                .map(user => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => navigate(`/admin/users/${user.id}`)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <TableCell>{user.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.name || user.givenName || 'N/A'}
                      {user.isSuperAdmin && (
                        <Chip icon={<AdminIcon />} label="Admin" color="error" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.affiliation || 'N/A'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit User">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditUser(user);
                        }}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TextField
          fullWidth
          placeholder="Search groups by name or description..."
          value={groupSearch}
          onChange={(e) => setGroupSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Public</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell align="center">Collections</TableCell>
                <TableCell>Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups
                .filter(group =>
                  groupSearch === '' ||
                  group.name?.toLowerCase().includes(groupSearch.toLowerCase()) ||
                  group.description?.toLowerCase().includes(groupSearch.toLowerCase())
                )
                .map(group => (
                <TableRow 
                  key={group.id} 
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => navigate(`/groups/${group.id}`)}
                >
                  <TableCell>{group.id}</TableCell>
                  <TableCell>{group.name}</TableCell>
                  <TableCell>{group.description?.substring(0, 50) || 'N/A'}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={group.isPublic ? 'Public' : 'Private'} 
                      size="small"
                      color={group.isPublic ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">{group.memberCount}</TableCell>
                  <TableCell align="center">{group.collectionCount}</TableCell>
                  <TableCell>{group.createdByUserName || 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Feature Toggles</Typography>
          <Button variant="contained" onClick={() => setToggleDialogOpen(true)} sx={{ mb: 2 }}>
            Add Feature Toggle
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Feature Name</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell align="center">Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {featureToggles.map((toggle) => (
                  <TableRow key={toggle.id}>
                    <TableCell>{toggle.featureName}</TableCell>
                    <TableCell>{toggle.groupName}</TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={toggle.enabled}
                        onChange={() => handleToggleFeature(toggle.id, toggle.enabled)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleDeleteToggle(toggle.id)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>Featured Collections</Typography>
          <Button variant="contained" onClick={() => setFeaturedDialogOpen(true)} sx={{ mb: 2 }}>
            Add Featured Collection
          </Button>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Collection Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="center">Images</TableCell>
                  <TableCell align="center">Display Order</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {featuredCollections.map((featured) => (
                  <TableRow key={featured.id}>
                    <TableCell>{featured.collectionName}</TableCell>
                    <TableCell>{featured.collectionDescription?.substring(0, 50) || 'N/A'}</TableCell>
                    <TableCell align="center">{featured.imageCount}</TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        value={featured.displayOrder}
                        onChange={(e) => handleUpdateDisplayOrder(featured.id, parseInt(e.target.value))}
                        size="small"
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleRemoveFeatured(featured.id)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">System Announcements</Typography>
          <Button variant="contained" onClick={() => {
            setEditingAnnouncement(null);
            setAnnouncementDialogOpen(true);
          }}>
            Create Announcement
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Message</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcements.map((announcement) => (
                <TableRow key={announcement.id}>
                  <TableCell sx={{ maxWidth: 300 }}>{announcement.message}</TableCell>
                  <TableCell>
                    <Chip 
                      label={announcement.severity} 
                      size="small"
                      color={announcement.severity === 'error' ? 'error' : announcement.severity === 'warning' ? 'warning' : 'info'}
                    />
                  </TableCell>
                  <TableCell>{new Date(announcement.startDate).toLocaleString()}</TableCell>
                  <TableCell>{new Date(announcement.endDate).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={announcement.active}
                      onChange={() => handleToggleAnnouncementActive(announcement.id, announcement.active)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton onClick={() => {
                        setEditingAnnouncement(announcement);
                        setAnnouncementDialogOpen(true);
                      }} size="small" color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteAnnouncement(announcement.id)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Error Center Tab */}
      <TabPanel value={tabValue} index={4}>
        {errorLoading && errorLogs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Error Stats Cards */}
            {errorStats && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'error.main', width: 48, height: 48 }}>
                      <ErrorIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{errorStats.totalErrors || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Total Errors</Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                      <WarningIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{errorStats.unresolvedErrors || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Unresolved</Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                      <TrendingUpIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{errorStats.errorsLast7Days || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Last 7 Days</Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <PhoneIphoneIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{errorStats.iosErrorsLast7Days || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">iOS (7d)</Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{(errorStats.totalErrors || 0) - (errorStats.unresolvedErrors || 0)}</Typography>
                      <Typography variant="body2" color="text.secondary">Resolved</Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                      <SpeedIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{(errorStats.errorRateLast7Days || 0).toFixed(1)}%</Typography>
                      <Typography variant="body2" color="text.secondary">Error Rate (7d)</Typography>
                    </Box>
                  </CardContent>
                </Card>
                <Card sx={{ minWidth: 160, flex: 1 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h5">{errorStats.totalSuccessful || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Successful (All)</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Error Code Breakdown */}
            {errorStats?.byErrorCode && Object.keys(errorStats.byErrorCode).length > 0 && (
              <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(errorStats.byErrorCode).map(([code, count]: [string, any]) => (
                  <Chip
                    key={code}
                    label={`${code}: ${count}`}
                    size="small"
                    color={
                      code === 'FILE_TOO_LARGE' ? 'warning' :
                      code === 'INVALID_IMAGE' ? 'error' :
                      code === 'PROCESSING_TIMEOUT' ? 'info' :
                      code === 'SERVER_ERROR' ? 'error' :
                      code === 'SERVICE_UNAVAILABLE' ? 'error' :
                      'default'
                    }
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            {/* Daily Trend Chart */}
            {errorStats?.dailyTrend && errorStats.dailyTrend.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ShowChartIcon color="primary" />
                    <Typography variant="h6">Error Trend (Last 30 Days)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 120, overflowX: 'auto' }}>
                    {errorStats.dailyTrend.map((day: any, idx: number) => {
                      const maxCount = Math.max(...errorStats.dailyTrend.map((d: any) => d.count), 1);
                      const heightPct = (day.count / maxCount) * 100;
                      return (
                        <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 28 }}>
                          <Tooltip title={`${day.date}: ${day.count} errors`}>
                            <Box
                              sx={{
                                width: 20,
                                height: `${heightPct}%`,
                                minHeight: 2,
                                bgcolor: day.count > 5 ? 'error.main' : day.count > 0 ? 'warning.main' : 'grey.300',
                                borderRadius: 0.5,
                              }}
                            />
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Error Heatmap */}
            {heatmapData.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <GridOnIcon color="primary" />
                    <Typography variant="h6">Error Heatmap (Day × Hour)</Typography>
                  </Box>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto repeat(24, 1fr)', gap: '1px', minWidth: 700 }}>
                      <Box />
                      {Array.from({ length: 24 }, (_, h) => (
                        <Typography key={h} variant="caption" sx={{ textAlign: 'center', fontSize: '0.6rem' }}>{h}</Typography>
                      ))}
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, dayIdx) => {
                        const dayNum = dayIdx + 1;
                        return (
                          <React.Fragment key={dayIdx}>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', pr: 0.5 }}>{dayName}</Typography>
                            {Array.from({ length: 24 }, (_, h) => {
                              const cell = heatmapData.find(
                                (c: any) => parseInt(c.dayOfWeek) === dayNum && parseInt(c.hour) === h
                              );
                              const count = cell ? cell.count : 0;
                              const maxCount = Math.max(...heatmapData.map((c: any) => c.count), 1);
                              const intensity = count > 0 ? count / maxCount : 0;
                              return (
                                <Tooltip key={h} title={`${dayName} ${h}:00 — ${count} errors`}>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      aspectRatio: '1',
                                      minWidth: 20,
                                      bgcolor: count > 0
                                        ? `rgba(244, 67, 54, ${0.2 + intensity * 0.8})`
                                        : 'rgba(0, 0, 0, 0.04)',
                                      borderRadius: 0.5,
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Top Offending Users */}
            {topUsers.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6">Top Offending Users</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {topUsers.map((user: any, idx: number) => {
                      const maxCount = Math.max(...topUsers.map((u: any) => u.errorCount), 1);
                      const displayName = user.userEmail && user.userEmail !== 'UNKNOWN'
                        ? user.userEmail
                        : user.userSubId || 'UNKNOWN';
                      return (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ minWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayName}
                          </Typography>
                          <Box sx={{ flex: 1, bgcolor: 'grey.200', borderRadius: 1, height: 20, position: 'relative' }}>
                            <Box
                              sx={{
                                width: `${(user.errorCount / maxCount) * 100}%`,
                                height: '100%',
                                bgcolor: 'error.main',
                                borderRadius: 1,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>{user.errorCount}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Error Rate Summary */}
            {errorRate && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SpeedIcon color="primary" />
                    <Typography variant="h6">Error Rate vs Total Requests</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Last 24 Hours', errors: errorRate.errors24h, total: errorRate.total24h, rate: errorRate.errorRate24h },
                      { label: 'Last 7 Days', errors: errorRate.errors7d, total: errorRate.total7d, rate: errorRate.errorRate7d },
                      { label: 'Last 30 Days', errors: errorRate.errors30d, total: errorRate.total30d, rate: errorRate.errorRate30d },
                    ].map((period, idx) => (
                      <Box key={idx} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{period.label}</Typography>
                        <Typography variant="h4" color={period.rate > 10 ? 'error.main' : period.rate > 5 ? 'warning.main' : 'success.main'}>
                          {(period.rate || 0).toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {period.errors} errors / {period.total} total
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={errorTimeRange}
                  label="Time Range"
                  onChange={(e) => { setErrorTimeRange(e.target.value); setErrorPage(0); }}
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="1h">Last 1 hour</MenuItem>
                  <MenuItem value="6h">Last 6 hours</MenuItem>
                  <MenuItem value="24h">Last 24 hours</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="User Email / Sub ID"
                value={errorFilters.userEmail}
                onChange={(e) => setErrorFilters({ ...errorFilters, userEmail: e.target.value })}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Error Code</InputLabel>
                <Select
                  value={errorFilters.errorCode}
                  label="Error Code"
                  onChange={(e) => setErrorFilters({ ...errorFilters, errorCode: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  {errorStats?.byErrorCode && Object.keys(errorStats.byErrorCode).map((code: string) => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                  <MenuItem value="INVALID_IMAGE">INVALID_IMAGE</MenuItem>
                  <MenuItem value="FILE_TOO_LARGE">FILE_TOO_LARGE</MenuItem>
                  <MenuItem value="PROCESSING_TIMEOUT">PROCESSING_TIMEOUT</MenuItem>
                  <MenuItem value="SERVER_ERROR">SERVER_ERROR</MenuItem>
                  <MenuItem value="SERVICE_UNAVAILABLE">SERVICE_UNAVAILABLE</MenuItem>
                  <MenuItem value="CLIENT_ERROR">CLIENT_ERROR</MenuItem>
                  <MenuItem value="FILE_READ_ERROR">FILE_READ_ERROR</MenuItem>
                  <MenuItem value="UNEXPECTED_ERROR">UNEXPECTED_ERROR</MenuItem>
                  <MenuItem value="MULTIPART_ERROR">MULTIPART_ERROR</MenuItem>
                  <MenuItem value="CLIENT_DISCONNECTED">CLIENT_DISCONNECTED</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Error Type</InputLabel>
                <Select
                  value={errorFilters.errorType}
                  label="Error Type"
                  onChange={(e) => setErrorFilters({ ...errorFilters, errorType: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="CLIENT_ERROR">CLIENT_ERROR</MenuItem>
                  <MenuItem value="SERVER_ERROR">SERVER_ERROR</MenuItem>
                  <MenuItem value="CONNECTION_ERROR">CONNECTION_ERROR</MenuItem>
                  <MenuItem value="IO_ERROR">IO_ERROR</MenuItem>
                  <MenuItem value="UNEXPECTED">UNEXPECTED</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Resolved</InputLabel>
                <Select
                  value={errorFilters.isResolved}
                  label="Resolved"
                  onChange={(e) => setErrorFilters({ ...errorFilters, isResolved: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="false">Unresolved</MenuItem>
                  <MenuItem value="true">Resolved</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={errorFilters.isIos}
                  label="Platform"
                  onChange={(e) => setErrorFilters({ ...errorFilters, isIos: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">iOS</MenuItem>
                  <MenuItem value="false">Other</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={errorFilters.isSuccess}
                  label="Status"
                  onChange={(e) => setErrorFilters({ ...errorFilters, isSuccess: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="false">Errors Only</MenuItem>
                  <MenuItem value="true">Successes Only</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Correlation ID"
                value={errorFilters.correlationId}
                onChange={(e) => setErrorFilters({ ...errorFilters, correlationId: e.target.value })}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={errorFilters.errorSource}
                  label="Source"
                  onChange={(e) => setErrorFilters({ ...errorFilters, errorSource: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ANALYSIS">Analysis</MenuItem>
                  <MenuItem value="CLIENT">Client</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={errorFilters.startDate}
                onChange={(e) => setErrorFilters({ ...errorFilters, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={errorFilters.endDate}
                onChange={(e) => setErrorFilters({ ...errorFilters, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <Button variant="contained" size="small" onClick={handleApplyErrorFilters} startIcon={<SearchIcon />}>
                Filter
              </Button>
              <Button variant="outlined" size="small" onClick={handleClearErrorFilters}>
                Clear
              </Button>
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={() => refetchErrorLogs()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Auto-refresh</Typography>}
              />
              <Tooltip title="Export CSV">
                <IconButton size="small" onClick={handleExportCSV}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* View Toggle */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant={groupedView ? 'outlined' : 'contained'}
                onClick={() => { setGroupedView(false); }}
              >
                Individual Logs
              </Button>
              <Button
                size="small"
                variant={groupedView ? 'contained' : 'outlined'}
                onClick={() => { setGroupedView(true); fetchGroupedErrors(); }}
              >
                Grouped Errors
              </Button>
            </Box>

            {/* Grouped Errors Table */}
            {groupedView ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Error Code</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell align="center">Count</TableCell>
                      <TableCell align="center">Unresolved</TableCell>
                      <TableCell>First Occurrence</TableCell>
                      <TableCell>Last Occurrence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedErrors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>
                            No grouped errors. Click "Grouped Errors" to load.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      groupedErrors.map((group, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Chip label={group.errorCode} size="small" color="error" />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {group.errorMessage}
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight="bold">{group.count}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            {group.unresolvedCount > 0 ? (
                              <Chip label={group.unresolvedCount} size="small" color="warning" />
                            ) : (
                              <CheckCircleIcon fontSize="small" color="success" />
                            )}
                          </TableCell>
                          <TableCell>{group.firstOccurrence ? new Date(group.firstOccurrence).toLocaleString() : 'N/A'}</TableCell>
                          <TableCell>{group.lastOccurrence ? new Date(group.lastOccurrence).toLocaleString() : 'N/A'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
            <>
            {/* Error Logs Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Error Code</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell align="center">Dimensions</TableCell>
                    <TableCell align="center">Size</TableCell>
                    <TableCell align="center">Time</TableCell>
                    <TableCell align="center">iOS</TableCell>
                    <TableCell align="center">Source</TableCell>
                    <TableCell align="center">Resolved</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No error logs found. Click Filter to search.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    errorLogs.map((log: any) => (
                      <TableRow
                        key={log.id}
                        hover
                        onClick={() => handleErrorResponse(log.id)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell>{log.id}</TableCell>
                        <TableCell>{log.createdAt ? new Date(log.createdAt).toLocaleString() : 'N/A'}</TableCell>
                        <TableCell>{log.userEmail || log.userSubId || 'N/A'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={log.isSuccess ? 'Success' : 'Error'}
                            size="small"
                            color={log.isSuccess ? 'success' : 'error'}
                            variant={log.isSuccess ? 'outlined' : 'filled'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.errorCode || 'N/A'}
                            size="small"
                            color={
                              log.errorCode === 'FILE_TOO_LARGE' ? 'warning' :
                              log.errorCode === 'INVALID_IMAGE' ? 'error' :
                              log.errorCode === 'SERVER_ERROR' ? 'error' :
                              log.errorCode === 'SERVICE_UNAVAILABLE' ? 'error' :
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.errorMessage || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.fileName || 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          {log.imageWidth && log.imageHeight ? `${log.imageWidth}x${log.imageHeight}` : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {log.fileSizeBytes ? `${(log.fileSizeBytes / 1024 / 1024).toFixed(1)}MB` : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {log.processingTimeMs ? `${(log.processingTimeMs / 1000).toFixed(1)}s` : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {log.isIos ? <PhoneIphoneIcon fontSize="small" color="primary" /> : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {log.errorSource ? (
                            <Chip
                              label={log.errorSource === 'CLIENT' ? 'Client' : 'Analysis'}
                              size="small"
                              variant="outlined"
                              color={log.errorSource === 'CLIENT' ? 'secondary' : 'info'}
                            />
                          ) : '—'}
                        </TableCell>
                        <TableCell align="center">
                          {log.isResolved ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <WarningIcon fontSize="small" color="warning" />
                          )}
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Delete">
                            <IconButton onClick={() => handleDeleteErrorLog(log.id)} size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {errorTotal > 0 && (() => {
              const totalPages = Math.ceil(errorTotal / errorRowsPerPage);
              const currentPage = errorPage;
              const maxPageButtons = 7;
              let startPage = Math.max(0, currentPage - Math.floor(maxPageButtons / 2));
              let endPage = Math.min(totalPages - 1, startPage + maxPageButtons - 1);
              if (endPage - startPage + 1 < maxPageButtons) {
                startPage = Math.max(0, endPage - maxPageButtons + 1);
              }
              const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
              return (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {errorPage * errorRowsPerPage + 1}–{Math.min((errorPage + 1) * errorRowsPerPage, errorTotal)} of {errorTotal}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 70 }}>
                      <Select
                        value={errorRowsPerPage}
                        onChange={(e) => { setErrorRowsPerPage(Number(e.target.value)); setErrorPage(0); }}
                      >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Button
                      size="small"
                      disabled={errorPage === 0}
                      onClick={() => setErrorPage(0)}
                    >
                      First
                    </Button>
                    <Button
                      size="small"
                      disabled={errorPage === 0}
                      onClick={() => setErrorPage(errorPage - 1)}
                    >
                      Prev
                    </Button>
                    {startPage > 0 && <Typography variant="body2" sx={{ px: 0.5 }}>…</Typography>}
                    {pageNumbers.map((pageNum) => (
                      <Button
                        key={pageNum}
                        size="small"
                        variant={pageNum === currentPage ? 'contained' : 'outlined'}
                        onClick={() => setErrorPage(pageNum)}
                        sx={{ minWidth: 36, px: 1 }}
                      >
                        {pageNum + 1}
                      </Button>
                    ))}
                    {endPage < totalPages - 1 && <Typography variant="body2" sx={{ px: 0.5 }}>…</Typography>}
                    <Button
                      size="small"
                      disabled={(errorPage + 1) * errorRowsPerPage >= errorTotal}
                      onClick={() => setErrorPage(errorPage + 1)}
                    >
                      Next
                    </Button>
                    <Button
                      size="small"
                      disabled={(errorPage + 1) * errorRowsPerPage >= errorTotal}
                      onClick={() => setErrorPage(totalPages - 1)}
                    >
                      Last
                    </Button>
                  </Box>
                </Box>
              );
            })()}
            </>
            )}
          </>
        )}
      </TabPanel>

      {/* Error Detail Dialog */}
      <Dialog open={errorDetailOpen} onClose={() => setErrorDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon color="error" />
            Error Detail #{errorDetail?.id}
          </Box>
        </DialogTitle>
        <DialogContent>
          {errorDetail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={errorDetail.errorCode || 'N/A'} color="error" size="small" />
                <Chip label={errorDetail.errorType || 'N/A'} size="small" variant="outlined" />
                <Chip label={errorDetail.httpStatus || 'N/A'} size="small" variant="outlined" />
                {errorDetail.errorSource && (
                  <Chip
                    label={errorDetail.errorSource === 'CLIENT' ? 'Client' : 'Analysis'}
                    size="small"
                    color={errorDetail.errorSource === 'CLIENT' ? 'secondary' : 'info'}
                    variant="outlined"
                  />
                )}
                {errorDetail.isIos && <Chip icon={<PhoneIphoneIcon />} label="iOS" size="small" color="primary" />}
                {errorDetail.isResolved ? (
                  <Chip icon={<CheckCircleIcon />} label="Resolved" size="small" color="success" />
                ) : (
                  <Chip icon={<WarningIcon />} label="Unresolved" size="small" color="warning" />
                )}
              </Box>

              <TextField label="User Email" value={errorDetail.userEmail || 'N/A'} size="small" fullWidth disabled />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="File Name" value={errorDetail.fileName || 'N/A'} size="small" fullWidth disabled />
                <TextField label="File Size" value={errorDetail.fileSizeBytes ? `${(errorDetail.fileSizeBytes / 1024 / 1024).toFixed(2)} MB` : 'N/A'} size="small" disabled />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Image Width" value={errorDetail.imageWidth || 'N/A'} size="small" disabled />
                <TextField label="Image Height" value={errorDetail.imageHeight || 'N/A'} size="small" disabled />
                <TextField label="Model" value={errorDetail.modelName || 'N/A'} size="small" disabled />
                <TextField label="Img Size" value={errorDetail.imgSize || 'N/A'} size="small" disabled />
              </Box>
              <TextField
                label="Error Message"
                value={errorDetail.errorMessage || 'N/A'}
                size="small"
                fullWidth
                multiline
                rows={3}
                disabled
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Client IP" value={errorDetail.clientIp || 'N/A'} size="small" disabled />
                <TextField label="Processing Time" value={errorDetail.processingTimeMs ? `${errorDetail.processingTimeMs}ms` : 'N/A'} size="small" disabled />
                <TextField label="Created At" value={errorDetail.createdAt ? new Date(errorDetail.createdAt).toLocaleString() : 'N/A'} size="small" disabled />
              </Box>
              <TextField label="User-Agent" value={errorDetail.userAgent || 'N/A'} size="small" fullWidth disabled />

              {/* Client Metadata */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField label="App Version" value={errorDetail.appVersion || 'N/A'} size="small" disabled />
                <TextField label="OS Version" value={errorDetail.osVersion || 'N/A'} size="small" disabled />
                <TextField label="Device Model" value={errorDetail.deviceModel || 'N/A'} size="small" disabled />
                <TextField label="Network Type" value={errorDetail.networkType || 'N/A'} size="small" disabled />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField label="Image Source" value={errorDetail.imageSource || 'N/A'} size="small" disabled />
                <TextField label="Image Format" value={errorDetail.imageFormat || 'N/A'} size="small" disabled />
                <TextField label="Retry Count" value={errorDetail.retryCount?.toString() || '0'} size="small" disabled />
                <TextField label="Stored File" value={errorDetail.storedFilePath || 'N/A'} size="small" disabled />
              </Box>
              {errorDetail.requestParams && (
                <TextField
                  label="Request Params"
                  value={errorDetail.requestParams}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  disabled
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
              )}

              {errorDetail.clientContext && (
                <TextField
                  label="Client Context"
                  value={errorDetail.clientContext}
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  disabled
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
              )}

              {errorDetail.clientMetrics && (
                <TextField
                  label="Client Metrics"
                  value={errorDetail.clientMetrics}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  disabled
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
              )}

              {errorDetail.correlationId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FingerprintIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">Correlation ID:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{errorDetail.correlationId}</Typography>
                  <Button
                    size="small"
                    startIcon={<CodeIcon />}
                    onClick={() => handleViewCorrelation(errorDetail.correlationId)}
                  >
                    View Related
                  </Button>
                </Box>
              )}

              {errorDetail.fastapiResponseBody && (
                <TextField
                  label="FastAPI Response Body"
                  value={errorDetail.fastapiResponseBody}
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  disabled
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
              )}

              {errorDetail.stackTrace && (
                <TextField
                  label="Stack Trace"
                  value={errorDetail.stackTrace}
                  size="small"
                  fullWidth
                  multiline
                  rows={6}
                  disabled
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                />
              )}

              {/* Replay Section */}
              {errorDetail.storedFilePath && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleReplay}
                      disabled={replayLoading}
                      startIcon={<RefreshIcon />}
                    >
                      {replayLoading ? 'Replaying...' : 'Replay Analysis'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Re-sends the stored file to FastAPI with the same parameters
                    </Typography>
                  </Box>
                  {replayResult && (
                    <Box sx={{ bgcolor: replayResult.error ? 'error.light' : 'success.light', p: 1, borderRadius: 1 }}>
                      {replayResult.error ? (
                        <Typography variant="body2" color="error">{replayResult.message}</Typography>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="success.main" gutterBottom>
                            Replay succeeded in {replayResult.replayProcessingTimeMs}ms
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Egg count: {replayResult.fastapiResponse?.egg_count ?? 'N/A'} | 
                            Processing time: {replayResult.fastapiResponse?.processing_time_seconds ?? 'N/A'}s
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {errorDetail.isResolved && (
                <>
                  <TextField label="Resolved By" value={errorDetail.resolvedBy || 'N/A'} size="small" disabled />
                  <TextField label="Resolved At" value={errorDetail.resolvedAt ? new Date(errorDetail.resolvedAt).toLocaleString() : 'N/A'} size="small" disabled />
                  <TextField label="Resolution Notes" value={errorDetail.resolutionNotes || ''} size="small" fullWidth multiline rows={2} disabled />
                </>
              )}

              {!errorDetail.isResolved && (
                <TextField
                  label="Resolution Notes"
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add notes about how this error was resolved..."
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDetailOpen(false)}>Close</Button>
          {errorDetail && !errorDetail.isResolved && (
            <Button variant="contained" color="success" onClick={handleResolveError} startIcon={<CheckCircleIcon />}>
              Mark Resolved
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Correlation Logs Dialog */}
      <Dialog open={correlationOpen} onClose={() => setCorrelationOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FingerprintIcon color="primary" />
            Correlation Logs
          </Box>
        </DialogTitle>
        <DialogContent>
          {correlationLogs.length === 0 ? (
            <Typography color="text.secondary">No logs found for this correlation ID.</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Success</TableCell>
                    <TableCell>Error Code</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Processing Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {correlationLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>{log.id}</TableCell>
                      <TableCell>{log.createdAt ? new Date(log.createdAt).toLocaleString() : 'N/A'}</TableCell>
                      <TableCell>
                        {log.isSuccess ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <ErrorIcon fontSize="small" color="error" />
                        )}
                      </TableCell>
                      <TableCell>{log.errorCode || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.errorMessage || '—'}
                      </TableCell>
                      <TableCell>{log.processingTimeMs ? `${log.processingTimeMs}ms` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrelationOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Given Name"
              value={editFormData.givenName}
              onChange={(e) => setEditFormData({ ...editFormData, givenName: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="affiliation-label">Affiliation</InputLabel>
              <Select
                labelId="affiliation-label"
                value={editFormData.affiliation}
                onChange={(e) => setEditFormData({ ...editFormData, affiliation: e.target.value })}
                label="Affiliation"
              >
                <MenuItem value="">
                  <em>Select University</em>
                </MenuItem>
                {universityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Email"
              value={editingUser?.email || ''}
              disabled
              fullWidth
              helperText="Email cannot be changed"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.isSuperAdmin}
                  onChange={(e) => setEditFormData({ ...editFormData, isSuperAdmin: e.target.checked })}
                  color="error"
                />
              }
              label="Super Admin"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveUser} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Feature Toggle Dialog */}
      <Dialog open={toggleDialogOpen} onClose={() => setToggleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Feature Toggle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Feature Name</InputLabel>
              <Select
                value={newToggle.featureName}
                onChange={(e) => setNewToggle({ ...newToggle, featureName: e.target.value })}
                label="Feature Name"
              >
                <MenuItem value="featured_collections">Featured Collections</MenuItem>
                <MenuItem value="groups">Groups</MenuItem>
                <MenuItem value="invites">Invites</MenuItem>
                <MenuItem value="collections">Collections</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Audience</InputLabel>
              <Select
                value={newToggle.groupId}
                onChange={(e) => setNewToggle({ ...newToggle, groupId: e.target.value })}
                label="Audience"
              >
                <MenuItem value="__ALL__">All Users</MenuItem>
                <MenuItem value="__SUPER_ADMIN__">Super Admins Only</MenuItem>
                <MenuItem disabled>─────────────────</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={newToggle.enabled}
                  onChange={(e) => setNewToggle({ ...newToggle, enabled: e.target.checked })}
                  color="primary"
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreateToggle} variant="contained" disabled={saving || !newToggle.groupId}>
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Featured Collection Dialog */}
      <Dialog open={featuredDialogOpen} onClose={() => setFeaturedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Featured Collection</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Collection</InputLabel>
              <Select
                value={newFeatured.collectionId}
                onChange={(e) => setNewFeatured({ ...newFeatured, collectionId: e.target.value })}
                label="Collection"
              >
                {allCollections.map((collection: any) => (
                  <MenuItem key={collection.id} value={collection.id}>
                    {collection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Display Order"
              type="number"
              value={newFeatured.displayOrder}
              onChange={(e) => setNewFeatured({ ...newFeatured, displayOrder: parseInt(e.target.value) })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeaturedDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleAddFeatured} variant="contained" disabled={saving || !newFeatured.collectionId}>
            {saving ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Announcement Dialog */}
      <Dialog open={announcementDialogOpen} onClose={() => {
        setAnnouncementDialogOpen(false);
        setEditingAnnouncement(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Message"
              value={editingAnnouncement ? editingAnnouncement.message : newAnnouncement.message}
              onChange={(e) => editingAnnouncement 
                ? setEditingAnnouncement({ ...editingAnnouncement, message: e.target.value })
                : setNewAnnouncement({ ...newAnnouncement, message: e.target.value })
              }
              fullWidth
              multiline
              rows={3}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={editingAnnouncement ? editingAnnouncement.severity : newAnnouncement.severity}
                onChange={(e) => editingAnnouncement
                  ? setEditingAnnouncement({ ...editingAnnouncement, severity: e.target.value })
                  : setNewAnnouncement({ ...newAnnouncement, severity: e.target.value })
                }
                label="Severity"
              >
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start Date"
              type="datetime-local"
              value={editingAnnouncement 
                ? new Date(editingAnnouncement.startDate).toISOString().slice(0, 16)
                : newAnnouncement.startDate
              }
              onChange={(e) => editingAnnouncement
                ? setEditingAnnouncement({ ...editingAnnouncement, startDate: e.target.value })
                : setNewAnnouncement({ ...newAnnouncement, startDate: e.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="End Date"
              type="datetime-local"
              value={editingAnnouncement 
                ? new Date(editingAnnouncement.endDate).toISOString().slice(0, 16)
                : newAnnouncement.endDate
              }
              onChange={(e) => editingAnnouncement
                ? setEditingAnnouncement({ ...editingAnnouncement, endDate: e.target.value })
                : setNewAnnouncement({ ...newAnnouncement, endDate: e.target.value })
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editingAnnouncement ? editingAnnouncement.active : newAnnouncement.active}
                  onChange={(e) => editingAnnouncement
                    ? setEditingAnnouncement({ ...editingAnnouncement, active: e.target.checked })
                    : setNewAnnouncement({ ...newAnnouncement, active: e.target.checked })
                  }
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAnnouncementDialogOpen(false);
            setEditingAnnouncement(null);
          }} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={editingAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? 'Saving...' : editingAnnouncement ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
